import React, { useEffect, useMemo, useState, useContext } from 'react';
import { AuthContext, API } from '@/App';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { MessageCircle, Send, Package } from 'lucide-react';
import { toast } from 'sonner';

const timeAgo = (dateString) => {
  if (!dateString) return '';
  const diff = Date.now() - new Date(dateString).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  return `${days}d`;
};

const Messages = () => {
  const { user } = useContext(AuthContext);
  const token = localStorage.getItem('token');

  const [conversations, setConversations] = useState([]);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const convoIdParam = new URLSearchParams(window.location.search).get('conversation');

  const [loadingConvos, setLoadingConvos] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [draft, setDraft] = useState('');

  const selectedId = selected?.id;

  const selectedTitle = useMemo(() => {
    if (!selected) return '';
    return `${selected.other_user_name || 'User'} · ${selected.product_title || 'Listing'}`;
  }, [selected]);

  const fetchConversations = async () => {
    try {
      const res = await axios.get(`${API}/messages/conversations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConversations(res.data);
      if (!selected && res.data.length) {
        const preferred = convoIdParam ? res.data.find((c) => c.id === convoIdParam) : null;
        setSelected(preferred || res.data[0]);
      }
    } catch {
      toast.error('Failed to load messages');
    } finally {
      setLoadingConvos(false);
    }
  };

  const fetchMessages = async (conversationId) => {
    try {
      setLoadingMessages(true);
      const res = await axios.get(`${API}/messages/conversations/${conversationId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(res.data);
    } catch {
      toast.error('Failed to load conversation');
    } finally {
      setLoadingMessages(false);
    }
  };

  useEffect(() => {
    if (!token) return;
    fetchConversations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    if (!selectedId) return;
    fetchMessages(selectedId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!draft.trim() || !selectedId) return;
    const content = draft.trim();
    setDraft('');
    try {
      const res = await axios.post(
        `${API}/messages/conversations/${selectedId}`,
        { content },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessages((prev) => [...prev, res.data]);
    } catch {
      setDraft(content);
      toast.error('Failed to send message');
    }
  };

  if (!token) {
    return (
      <div className="max-w-3xl mx-auto">
        <div className="panel">
          <div className="panel-header">
            <div>
              <div className="panel-title">Messages</div>
              <h1 className="text-2xl md:text-3xl font-semibold mt-2">Please log in</h1>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="messages-page" className="max-w-5xl mx-auto space-y-4">
      {/* Compact header */}
      <div className="flex items-center gap-3">
        <MessageCircle className="w-5 h-5 text-[var(--accent-primary)]" />
        <h1 className="text-xl font-semibold text-[#292524]">Messages</h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-3" style={{ height: '520px' }}>
        {/* Thread list */}
        <Card className="lg:col-span-2 bg-white/80 backdrop-blur-sm border-[#D6D3D1] flex flex-col overflow-hidden">
          <CardContent className="p-0 flex-1 overflow-auto">
            {loadingConvos ? (
              <div className="p-4 text-sm text-[#78716C]">Loading…</div>
            ) : conversations.length === 0 ? (
              <div className="p-4 text-sm text-[#78716C]">No conversations yet.</div>
            ) : (
              conversations.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => setSelected(c)}
                  className={`w-full text-left px-4 py-3 border-b border-[#E7E5E4] transition-colors ${
                    selected?.id === c.id
                      ? 'bg-[#334155] text-white'
                      : 'hover:bg-[#F5F5F4]'
                  }`}
                >
                  <div className="flex items-center justify-between gap-2 mb-0.5">
                    <span className={`text-sm font-medium truncate ${selected?.id === c.id ? 'text-white' : 'text-[#292524]'}`}>
                      {c.other_user_name}
                    </span>
                    <span className={`text-xs flex-shrink-0 ${selected?.id === c.id ? 'text-zinc-300' : 'text-[#A8A29E]'}`}>
                      {timeAgo(c.updated_at)}
                    </span>
                  </div>
                  <div className={`flex items-center gap-1 text-xs truncate ${selected?.id === c.id ? 'text-zinc-300' : 'text-[#78716C]'}`}>
                    <Package className="w-3 h-3 flex-shrink-0" />
                    <span className="truncate">{c.product_title}</span>
                  </div>
                </button>
              ))
            )}
          </CardContent>
        </Card>

        {/* Message thread */}
        <Card className="lg:col-span-3 bg-white/80 backdrop-blur-sm border-[#D6D3D1] flex flex-col overflow-hidden">
          {/* Thread title */}
          <div className="px-4 py-3 border-b border-[#E7E5E4] flex-shrink-0">
            <div className="text-sm font-medium text-[#292524] truncate">
              {selectedTitle || 'Select a thread'}
            </div>
          </div>

          {/* Messages */}
          <ScrollArea data-testid="dm-messages" className="flex-1 p-3">
            {loadingMessages ? (
              <div className="text-sm text-[#78716C] p-2">Loading…</div>
            ) : !selected ? (
              <div className="text-sm text-[#78716C] p-2">Choose a conversation.</div>
            ) : messages.length === 0 ? (
              <div className="text-sm text-[#78716C] p-2">No messages yet. Say hello!</div>
            ) : (
              <div className="space-y-2">
                {messages.map((m) => {
                  const isMine = m.sender_id === user?.id;
                  return (
                    <div key={m.id} className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[78%] px-3 py-2 rounded-lg text-sm ${
                        isMine
                          ? 'bg-[#B45309]/15 border border-[#B45309]/20 text-[#292524]'
                          : 'bg-[#F5F5F4] border border-[#E7E5E4] text-[#292524]'
                      }`}>
                        {!isMine && (
                          <div className="text-xs text-[#78716C] font-medium mb-0.5">{m.sender_name}</div>
                        )}
                        <div className="whitespace-pre-wrap leading-snug">{m.content}</div>
                        <div className="text-xs text-[#A8A29E] mt-1 text-right">{timeAgo(m.created_at)}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </ScrollArea>

          {/* Compose */}
          <form onSubmit={handleSend} className="px-3 pb-3 pt-2 border-t border-[#E7E5E4] flex gap-2 flex-shrink-0">
            <Input
              data-testid="dm-input"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder={selected ? 'Type a message…' : 'Select a thread first'}
              disabled={!selected}
              className="bg-white border-[#D6D3D1] text-[#292524] text-sm"
            />
            <Button
              data-testid="dm-send"
              type="submit"
              disabled={!selected || !draft.trim()}
              className="bg-[#10B981] hover:bg-[#10B981]/90 text-white px-3"
            >
              <Send className="w-4 h-4" />
            </Button>
          </form>
        </Card>
      </div>
    </div>
  );
};

export default Messages;
